import {
  Box,
  getIndicator,
  Inline,
  Stack,
  Text,
} from '@spaceship-fspl/components';
import { useBoolean } from '@spaceship-fspl/dynamic-config';
import { DatedBalance } from '@spaceship-fspl/graphql';
import { formatCurrencyWithCodeAndSymbol } from '@spaceship-fspl/helpers';
import {
  DATE_FORMAT_SHORT_MONTH_NAME,
  formatDate,
  formatPercentage,
  humanReadable,
} from '@spaceship-fspl/helpers';
import { FeatureFlagKeys } from 'data/spaceship/dynamic-config';
import React from 'react';
import styled from 'styled-components';

interface Instrument {
  id: string;
  name: string;
  audLatestPrice: string;
  audMarketCapitalisation: string;
  ytdPercentageChange: string;
  ytdPercentageHigh: string;
  ytdPercentageLow: string;
  targetAllocationPercentage: string | null;
}

interface CompanyStatisticsProps {
  company?: Instrument;
  prices?: Array<DatedBalance>;
}

export const getTodayPerformance = (
  prices?: Array<DatedBalance>,
): number | undefined =>
  prices && prices?.length >= 2
    ? prices
        .slice()
        .sort((p1, p2) =>
          p1.date && p2.date ? Date.parse(p1.date) - Date.parse(p2.date) : 0,
        )
        .slice(-2)
        .map(({ audAmount }) => Number(audAmount))
        .reduce((p1, p2) => ((p2 - p1) / p1) * 100)
    : undefined;

const Label = styled(Text).attrs({
  variant: 3,
  color: 'neutral.080',
})`
  font-weight: 500;
`;

const LabelledField: React.FC<
  React.PropsWithChildren<{
    label: string;
    value?: string | number;
    indicator?: (value?: string | number) => React.ReactElement | null;
    format?: (value: string | number) => string;
  }>
> = ({ label, value, indicator = () => null, format = (x) => x }) => (
  <Stack>
    <Label>{label}</Label>
    <Inline spaceX="xs">
      {indicator(value)}
      <Text variant={1}>{value ? format(value) : '—'}</Text>
    </Inline>
  </Stack>
);

export const CompanyStatistics: React.FC<
  React.PropsWithChildren<CompanyStatisticsProps>
> = ({ company, prices }) => {
  const showDailyChange = useBoolean(
    FeatureFlagKeys.VOYAGER_GRAPH_PERCENTAGE_CHANGES_ENABLED,
    false,
  );
  const todayPerformance = getTodayPerformance(prices);
  const latestMarketPrice = prices?.[prices?.length - 1];

  return (
    <>
      <Inline
        spaceX={{ xs: 'sm', sm: 'md', md: 'lg' }}
        spaceY={{ xs: 'sm', sm: 'md' }}
      >
        {showDailyChange ? (
          <LabelledField
            label="Daily change"
            value={Number(todayPerformance)}
            format={(value) => formatPercentage(value, 2).replace(/-/g, '')}
            indicator={getIndicator}
          />
        ) : null}
        <LabelledField
          label="Share price"
          value={formatCurrencyWithCodeAndSymbol(company?.audLatestPrice)}
        />
        <LabelledField
          label="Asset size"
          value={
            company?.audMarketCapitalisation
              ? `A${humanReadable(
                  parseInt(company.audMarketCapitalisation),
                  2,
                )}`
              : '—'
          }
        />
        <LabelledField
          label="Target allocation"
          value={formatPercentage(company?.targetAllocationPercentage, 2)}
        />
      </Inline>
      {!!latestMarketPrice && (
        <Box display="flex" justifyContent={{ md: 'flex-end' }} marginTop="xs">
          <Text variant={4} align="center" color="neutral.080">
            Price is in AUD and performance as at{' '}
            {formatDate(latestMarketPrice.date, DATE_FORMAT_SHORT_MONTH_NAME)}
          </Text>
        </Box>
      )}
    </>
  );
};
